import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, MapPin, Car, Plus, LogOut, RefreshCw, XCircle, Pencil } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { formatUkDateTime, formatUkDate } from '../lib/date';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_PILL = {
  quote_requested: { label: 'Awaiting Quote', cls: 'bg-amber-100 text-amber-800' },
  quote_sent: { label: 'Quote Sent', cls: 'bg-blue-100 text-blue-800' },
  quote_accepted: { label: 'Confirmed', cls: 'bg-emerald-100 text-emerald-800' },
  confirmed: { label: 'Confirmed', cls: 'bg-emerald-100 text-emerald-800' },
  modification_pending: { label: 'Modification Pending', cls: 'bg-amber-100 text-amber-800' },
  in_progress: { label: 'In Progress', cls: 'bg-purple-100 text-purple-800' },
  completed: { label: 'Completed', cls: 'bg-gray-100 text-gray-800' },
  cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-800' },
};

const StatusPill = ({ status }) => {
  const cfg = STATUS_PILL[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
  return <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${cfg.cls}`}>{cfg.label}</span>;
};

const MyTrips = () => {
  const { customer, logout, loading: authLoading, isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: '/my-trips' } });
    }
  }, [authLoading, isAuthenticated, navigate]);

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/customer/bookings`);
      setBookings(data.bookings || []);
    } catch {
      toast.error('Could not load your bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) fetchBookings();
  }, [isAuthenticated, fetchBookings]);

  const { upcoming, past } = useMemo(() => {
    const now = new Date();
    const u = []; const p = [];
    for (const b of bookings) {
      const dt = new Date(`${b.pickupDate}T${b.pickupTime || '00:00'}`);
      const isFinished = ['completed', 'cancelled'].includes(b.status);
      if (!isFinished && dt >= now) u.push(b); else p.push(b);
    }
    u.sort((a, b) => `${a.pickupDate}T${a.pickupTime}`.localeCompare(`${b.pickupDate}T${b.pickupTime}`));
    p.sort((a, b) => `${b.pickupDate}T${b.pickupTime}`.localeCompare(`${a.pickupDate}T${a.pickupTime}`));
    return { upcoming: u, past: p };
  }, [bookings]);

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this booking? This cannot be undone.')) return;
    setCancellingId(id);
    try {
      await axios.post(`${API}/bookings/${id}/cancel`);
      toast.success('Booking cancelled');
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel');
    } finally {
      setCancellingId('');
    }
  };

  const handleRebook = (b) => {
    // The Booking page expects lowercase service types ('transfer' / 'as-directed').
    const svc = (b.serviceType || '').toLowerCase().includes('directed') ? 'as-directed' : 'transfer';
    navigate('/booking', {
      state: {
        prefill: {
          serviceType: svc,
          pickupLocation: b.pickupLocation,
          dropoffLocation: b.dropoffLocation,
          vehicle: b.vehicle,
          vehicleName: b.vehicleName,
          passengers: b.passengers,
          luggage: b.luggage,
        },
      },
    });
  };

  if (authLoading || !customer) {
    return <div className="min-h-screen pt-32 px-6 text-center text-gray-500">Loading…</div>;
  }

  const TripCard = ({ b, allowActions }) => (
    <Card className="p-5" data-testid={`trip-card-${b.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-xs text-gray-500">Booking ref</p>
          <p className="font-bold text-black tracking-wide">{b.id}</p>
        </div>
        <StatusPill status={b.status} />
      </div>
      <div className="space-y-2 text-sm">
        <div className="flex items-start gap-2 text-gray-700">
          <Calendar className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
          <span>{formatUkDateTime(b.pickupDate, b.pickupTime)}</span>
        </div>
        <div className="flex items-start gap-2 text-gray-700">
          <MapPin className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
          <div className="min-w-0">
            <p className="truncate">{b.pickupLocation}</p>
            <p className="text-xs text-gray-500 truncate">→ {b.dropoffLocation}</p>
          </div>
        </div>
        {b.vehicleName && (
          <div className="flex items-start gap-2 text-gray-700">
            <Car className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
            <span>{b.vehicleName}</span>
          </div>
        )}
        {b.quoteAmount && (
          <p className="text-sm font-semibold text-black pt-1">£{Number(b.quoteAmount).toFixed(2)}</p>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
        {allowActions && (
          <>
            <Button asChild variant="outline" size="sm" className="border-gray-300" data-testid={`trip-modify-${b.id}`}>
              <Link to={`/booking/edit/${b.id}`}>
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Modify
              </Link>
            </Button>
            <Button
              variant="outline" size="sm"
              className="border-red-200 text-red-700 hover:bg-red-50"
              onClick={() => handleCancel(b.id)}
              disabled={cancellingId === b.id}
              data-testid={`trip-cancel-${b.id}`}
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" />
              {cancellingId === b.id ? 'Cancelling…' : 'Cancel'}
            </Button>
          </>
        )}
        <Button variant="outline" size="sm" className="border-gray-300" onClick={() => handleRebook(b)} data-testid={`trip-rebook-${b.id}`}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Book Again
        </Button>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen pt-32 pb-20 px-6 bg-gray-50">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-black">Hello, {customer.firstName}</h1>
            <p className="text-gray-600 mt-1">{customer.email}</p>
          </div>
          <div className="flex gap-2">
            <Button asChild className="bg-black text-white hover:bg-gray-800" data-testid="new-trip-btn">
              <Link to="/booking"><Plus className="h-4 w-4 mr-1.5" /> New Booking</Link>
            </Button>
            <Button variant="outline" onClick={async () => { await logout(); toast.success('Signed out'); navigate('/'); }} data-testid="customer-logout">
              <LogOut className="h-4 w-4 mr-1.5" /> Sign out
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading your trips…</p>
        ) : (
          <>
            {/* Upcoming */}
            <section className="mb-12">
              <h2 className="text-lg font-bold text-black mb-4">Upcoming Trips</h2>
              {upcoming.length === 0 ? (
                <Card className="p-10 text-center text-gray-600">
                  <p className="mb-4">You have no upcoming trips.</p>
                  <Button asChild className="bg-black text-white hover:bg-gray-800">
                    <Link to="/booking">Book your first journey</Link>
                  </Button>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="upcoming-trips">
                  {upcoming.map((b) => <TripCard key={b.id} b={b} allowActions />)}
                </div>
              )}
            </section>

            {/* Past */}
            {past.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-black mb-4">Past Trips ({past.length})</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="past-trips">
                  {past.map((b) => <TripCard key={b.id} b={b} allowActions={false} />)}
                </div>
              </section>
            )}

            {bookings.length === 0 && (
              <p className="text-xs text-gray-500 mt-6">
                Heads-up: trips you booked before creating your account are auto-linked when the booking email
                matches your account email ({formatUkDate(customer.createdAt)?.slice(0, 10) || ''}).
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyTrips;
